import { DEFAULT_GEO } from './defaults';

const SCHEMA_ENTITY_MAP = {
  organization: 'Organization',
  localBusiness: 'LocalBusiness',
  person: 'Person',
  product: 'Product',
  service: 'Service',
  website: 'WebSite',
};

const AI_CRAWLER_BOTS = [
  'GPTBot',
  'ChatGPT-User',
  'Google-Extended',
  'anthropic-ai',
  'ClaudeBot',
  'Claude-Web',
  'PerplexityBot',
  'Applebot-Extended',
  'CCBot',
  'cohere-ai',
];

function normalizeGeo(geo) {
  return { ...DEFAULT_GEO, ...(geo || {}) };
}

function parseTopics(raw) {
  return String(raw || '')
    .split(/[,;\n]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function parseKeyFacts(raw) {
  return String(raw || '')
    .split('\n')
    .map((l) => l.replace(/^[-•*]\s*/, '').trim())
    .filter(Boolean);
}

/** Construit le JSON-LD Schema.org pour l'indexation IA. */
export function buildGeoJsonLd(site, pageUrl) {
  const geo = normalizeGeo(site?.geo);
  if (geo.enabled === false) return null;

  const pageTitle = site.seo?.shareTitle?.trim() || site.title || 'Page';
  const description =
    geo.summary?.trim() ||
    site.seo?.shareDescription?.trim() ||
    site.description?.trim() ||
    '';
  const topics = parseTopics(geo.topics);
  const keyFacts = parseKeyFacts(geo.keyFacts);
  const schemaType = SCHEMA_ENTITY_MAP[geo.entityType] || 'Organization';

  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    url: pageUrl,
    description: description || undefined,
    inLanguage: geo.language || 'fr',
    ...(topics.length ? { keywords: topics.join(', ') } : {}),
    ...(keyFacts.length
      ? {
          abstract: keyFacts.join(' · '),
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: keyFacts.map((fact, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: fact,
            })),
          },
        }
      : {}),
  };

  if (geo.audience?.trim()) {
    webPage.audience = {
      '@type': 'Audience',
      audienceType: geo.audience.trim(),
    };
  }

  const entityName = geo.entityName?.trim() || site.title?.trim();
  if (!entityName) return webPage;

  const entity = {
    '@type': schemaType,
    name: entityName,
    url: pageUrl,
    description: description || undefined,
  };

  if (schemaType === 'Product' && site.seo?.shareImageUrl) {
    entity.image = site.seo.shareImageUrl;
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [webPage, { ...entity, mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl } }],
  };
}

/** Meta robots pour bloquer ou autoriser les crawlers IA. */
export function getGeoRobotMetaTags(geo) {
  const g = normalizeGeo(geo);
  const tags = [];

  if (g.enabled === false) {
    tags.push({ name: 'robots', content: 'noindex, nofollow, noai, noimageai' });
    for (const bot of AI_CRAWLER_BOTS) {
      tags.push({ name: bot, content: 'noindex, nofollow' });
    }
    return tags;
  }

  if (!g.allowTraining) {
    for (const bot of AI_CRAWLER_BOTS) {
      tags.push({ name: bot, content: 'noindex, nofollow' });
    }
  }

  return tags;
}

/** Meta description enrichie pour les moteurs IA (résumé GEO). */
export function getGeoSummaryMeta(geo) {
  const g = normalizeGeo(geo);
  if (g.enabled === false || !g.summary?.trim()) return null;
  return { name: 'abstract', content: g.summary.trim() };
}

export function getGeoTopicsMeta(geo) {
  const g = normalizeGeo(geo);
  const topics = parseTopics(g.topics);
  if (g.enabled === false || !topics.length) return null;
  return { name: 'keywords', content: topics.join(', ') };
}
